import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Group,
  Text,
  Badge,
  NumberInput,
  ActionIcon,
  Card,
  Stack,
  Image,
  Button,
  Alert
} from '@mantine/core';
import {
  IconTrash,
  IconMinus,
  IconPlus,
  IconExclamationCircle
} from '@tabler/icons-react';
import { updateCartItemQuantity, removeFromCart } from '../../store/slices/cartSlice';

const CartItem = ({ item, compact = false }) => {
  const dispatch = useDispatch();
  const [isUpdating, setIsUpdating] = useState(false);

  const handleQuantityChange = async (newQuantity) => {
    if (newQuantity < 1) return;
    
    setIsUpdating(true);
    try {
      dispatch(updateCartItemQuantity({ 
        productId: item.id, 
        quantity: newQuantity 
      }));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRemove = () => {
    dispatch(removeFromCart(item.id));
  };

  const formatCondition = (condition) => {
    return condition.replace('_', ' ').split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getConditionColor = (condition) => {
    switch (condition) {
      case 'new': return 'green';
      case 'like_new': return 'pink';
      case 'used_excellent': return 'teal';
      case 'used_good': return 'yellow';
      case 'used_fair': return 'orange';
      case 'refurbished': return 'grape';
      default: return 'gray';
    }
  };

  const itemTotal = (parseFloat(item.price) * item.quantity).toFixed(2);
  const isOutOfStock = !item.is_available || item.quantity > item.stock_quantity;

  if (compact) {
    // Compact view for cart drawer
    return (
      <Group gap="md" wrap="nowrap">
        {/* Product Image */}
        <div style={{ width: 60, height: 60, flexShrink: 0 }}>
          {item.primary_image ? (
            <Image
              src={item.primary_image}
              alt={item.title}
              fit="cover"
              radius="sm"
              height={60}
              width={60}
            />
          ) : (
            <div style={{
              width: 60,
              height: 60,
              backgroundColor: '#f8f9fa',
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Text size="xs" c="dimmed">No Image</Text>
            </div>
          )}
        </div>

        {/* Product Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text 
            size="sm" 
            fw={500} 
            lineClamp={2}
            component={Link}
            to={`/products/${item.id}`}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            {item.title}
          </Text>
          
          <Group gap="xs" mt="xs">
            <Badge color={getConditionColor(item.condition)} variant="light" size="xs">
              {formatCondition(item.condition)}
            </Badge>
            <Text size="xs" c="dimmed">
              by {item.seller.business_display_name}
            </Text>
          </Group>

          <Group gap="md" mt="xs" align="center">
            <Group gap="xs" align="center">
              <ActionIcon
                size="xs"
                variant="subtle"
                onClick={() => handleQuantityChange(item.quantity - 1)}
                disabled={isUpdating || item.quantity <= 1}
              >
                <IconMinus size="0.8rem" />
              </ActionIcon>
              
              <Text size="sm" fw={500} style={{ minWidth: 20, textAlign: 'center' }}>
                {item.quantity}
              </Text>
              
              <ActionIcon
                size="xs"
                variant="subtle"
                onClick={() => handleQuantityChange(item.quantity + 1)}
                disabled={isUpdating || item.quantity >= item.stock_quantity}
              >
                <IconPlus size="0.8rem" />
              </ActionIcon>
            </Group>

            <Text size="sm" fw={600} c="pink">
              ${itemTotal}
            </Text>

            <ActionIcon
              size="xs"
              color="red"
              variant="subtle"
              onClick={handleRemove}
            >
              <IconTrash size="0.8rem" />
            </ActionIcon>
          </Group>

          {isOutOfStock && (
            <Text size="xs" c="red" mt="xs">
              {!item.is_available ? 'No longer available' : 'Insufficient stock'}
            </Text>
          )}
        </div>
      </Group>
    );
  }

  // Full view for cart page
  return (
    <Card shadow="sm" padding="md" radius="md" withBorder>
      {isOutOfStock && (
        <Alert 
          icon={<IconExclamationCircle size="1rem" />} 
          color="red" 
          variant="light"
          mb="md"
        >
          {!item.is_available ? 
            'This item is no longer available' : 
            `Only ${item.stock_quantity} left in stock`
          }
        </Alert>
      )}

      <Group align="flex-start" wrap="nowrap">
        {/* Product Image */}
        <div style={{ width: 120, height: 120, flexShrink: 0 }}>
          {item.primary_image ? (
            <Image
              src={item.primary_image}
              alt={item.title}
              fit="cover"
              radius="md"
              height={120}
              width={120}
            />
          ) : (
            <div style={{
              width: 120,
              height: 120,
              backgroundColor: '#f8f9fa',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Text c="dimmed">No Image</Text>
            </div>
          )}
        </div>

        {/* Product Details */}
        <div style={{ flex: 1 }}>
          <Stack gap="sm">
            <div>
              <Text 
                size="lg" 
                fw={500}
                component={Link}
                to={`/products/${item.id}`}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                {item.title}
              </Text>
              
              <Group gap="sm" mt="xs">
                <Badge color={getConditionColor(item.condition)} variant="light">
                  {formatCondition(item.condition)}
                </Badge>
                <Text size="sm" c="dimmed">
                  Sold by {item.seller.business_display_name}
                </Text>
                {item.seller.is_verified && (
                  <Badge color="green" variant="light" size="xs">
                    Verified Princess
                  </Badge>
                )}
              </Group>
            </div>

            <Group gap="lg" align="center">
              <div>
                <Text size="sm" c="dimmed" mb="xs">Quantity:</Text>
                <Group gap="xs" align="center">
                  <ActionIcon
                    variant="outline"
                    onClick={() => handleQuantityChange(item.quantity - 1)}
                    disabled={isUpdating || item.quantity <= 1}
                  >
                    <IconMinus size="1rem" />
                  </ActionIcon>
                  
                  <NumberInput
                    value={item.quantity}
                    onChange={(value) => handleQuantityChange(value)}
                    min={1}
                    max={item.stock_quantity}
                    style={{ width: 80 }}
                    disabled={isUpdating}
                  />
                  
                  <ActionIcon
                    variant="outline"
                    onClick={() => handleQuantityChange(item.quantity + 1)}
                    disabled={isUpdating || item.quantity >= item.stock_quantity}
                  >
                    <IconPlus size="1rem" />
                  </ActionIcon>
                </Group>
                <Text size="xs" c="dimmed" mt="xs">
                  {item.stock_quantity} available
                </Text>
              </div>

              <div>
                <Text size="sm" c="dimmed">Price:</Text>
                <Text size="lg" fw={600}>${item.price}</Text>
              </div>

              <div>
                <Text size="sm" c="dimmed">Total:</Text>
                <Text size="xl" fw={700} c="pink">${itemTotal}</Text>
              </div>

              <Button
                variant="subtle"
                color="red"
                leftSection={<IconTrash size="1rem" />}
                onClick={handleRemove}
              >
                Remove
              </Button>
            </Group>
          </Stack>
        </div>
      </Group>
    </Card>
  );
};

export default CartItem;